import { useState, useEffect, useCallback } from 'react'
import { lumi } from '@/lib/lumi'

export interface Accompagnement {
  _id?: string
  nom: string
  duree: string
  prix: number
  placesMax: number
  description: string
  inclus?: string[]
  miseEnAvant?: boolean
  actif: boolean
  ordre?: number
  dateDebut?: string
  dateFin?: string
  lieu?: string
  instructionsPreparation?: string
  creator?: string
  createdAt?: string
  updatedAt?: string
}

export interface Session {
  _id?: string
  accompagnementId: string
  accompagnementNom?: string
  dateDebut: string
  dateFin?: string
  lieu?: string
  prix?: number
  placesMax: number
  placesRestantes: number
  statut: 'ouverte' | 'complete' | 'annulee' | 'terminee'
  notes?: string
  creator?: string
  createdAt?: string
  updatedAt?: string
}

export interface Inscription {
  _id?: string
  sessionId: string
  accompagnementId?: string
  accompagnementNom?: string
  dateSession?: string
  nom: string
  email: string
  telephone?: string
  message?: string
  statutPaiement: 'en_attente' | 'paye' | 'rembourse' | 'annule'
  modePaiement?: 'cb' | 'virement' | 'especes' | 'autre'
  montant: number
  datePaiement?: string
  notes?: string
  creator?: string
  createdAt?: string
  updatedAt?: string
}

export function useAccompagnements() {
  const [accompagnements, setAccompagnements] = useState<Accompagnement[]>([])
  const [sessions, setSessions] = useState<Session[]>([])
  const [inscriptions, setInscriptions] = useState<Inscription[]>([])
  const [loading, setLoading] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [a, s, i] = await Promise.all([
        lumi.entities.accompagnements.list({ sort: { ordre: 1 } }),
        lumi.entities.sessions_accompagnement.list({ sort: { dateDebut: 1 } }),
        lumi.entities.inscriptions.list({ sort: { createdAt: -1 } }),
      ])
      setAccompagnements(a.list || [])
      setSessions(s.list || [])
      setInscriptions(i.list || [])
    } catch (err) {
      console.error('Fetch failed:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // Accompagnements CRUD
  const createAccompagnement = async (data: Partial<Accompagnement>) => {
    const now = new Date().toISOString()
    const payload = {
      nom: data.nom || '',
      duree: data.duree || '',
      prix: Number(data.prix) || 0,
      placesMax: Number(data.placesMax) || 1,
      description: data.description || '',
      inclus: data.inclus || [],
      miseEnAvant: !!data.miseEnAvant,
      actif: data.actif !== false,
      ordre: Number(data.ordre) || 0,
      dateDebut: data.dateDebut || '',
      dateFin: data.dateFin || '',
      lieu: data.lieu || '',
      instructionsPreparation: data.instructionsPreparation || '',
      creator: 'admin',
      createdAt: now,
      updatedAt: now,
    }
    await lumi.entities.accompagnements.create(payload)
    await fetchAll()
  }

  const updateAccompagnement = async (id: string, data: Partial<Accompagnement>) => {
    const payload: any = { ...data, updatedAt: new Date().toISOString() }
    if (data.prix !== undefined) payload.prix = Number(data.prix)
    if (data.placesMax !== undefined) payload.placesMax = Number(data.placesMax)
    if (data.ordre !== undefined) payload.ordre = Number(data.ordre)
    delete payload._id
    delete payload.creator
    delete payload.createdAt
    await lumi.entities.accompagnements.update(id, payload)
    await fetchAll()
  }

  const deleteAccompagnement = async (id: string) => {
    await lumi.entities.accompagnements.delete(id)
    await fetchAll()
  }

  // Sessions CRUD
  const createSession = async (data: Partial<Session>) => {
    const now = new Date().toISOString()
    const acc = accompagnements.find((a) => a._id === data.accompagnementId)
    const places = Number(data.placesMax) || acc?.placesMax || 1
    const payload = {
      accompagnementId: data.accompagnementId || '',
      accompagnementNom: acc?.nom || '',
      dateDebut: data.dateDebut || now,
      dateFin: data.dateFin || '',
      lieu: data.lieu || '',
      prix: Number(data.prix) || 0,
      placesMax: places,
      placesRestantes: places,
      statut: (data.statut || 'ouverte') as Session['statut'],
      notes: data.notes || '',
      creator: 'admin',
      createdAt: now,
      updatedAt: now,
    }
    await lumi.entities.sessions_accompagnement.create(payload)
    await fetchAll()
  }

  const updateSession = async (id: string, data: Partial<Session>) => {
    const payload: any = { ...data, updatedAt: new Date().toISOString() }
    if (data.placesMax !== undefined) payload.placesMax = Number(data.placesMax)
    if (data.placesRestantes !== undefined) payload.placesRestantes = Number(data.placesRestantes)
    if (data.prix !== undefined) payload.prix = Number(data.prix)
    delete payload._id
    delete payload.creator
    delete payload.createdAt
    await lumi.entities.sessions_accompagnement.update(id, payload)
    await fetchAll()
  }

  const deleteSession = async (id: string) => {
    await lumi.entities.sessions_accompagnement.delete(id)
    await fetchAll()
  }

  // Inscriptions CRUD
  const updateInscription = async (id: string, data: Partial<Inscription>) => {
    const payload: any = { ...data, updatedAt: new Date().toISOString() }
    if (data.montant !== undefined) payload.montant = Number(data.montant)
    delete payload._id
    delete payload.creator
    delete payload.createdAt
    await lumi.entities.inscriptions.update(id, payload)
    await fetchAll()
  }

  const deleteInscription = async (id: string) => {
    await lumi.entities.inscriptions.delete(id)
    await fetchAll()
  }

  return {
    accompagnements,
    sessions,
    inscriptions,
    loading,
    fetchAll,
    createAccompagnement,
    updateAccompagnement,
    deleteAccompagnement,
    createSession,
    updateSession,
    deleteSession,
    updateInscription,
    deleteInscription,
  }
}
